/**
 * Registry of agent-action *lifecycle* capabilities — the non-render half
 * of a permission-gated AI action (the render half lives in the UI
 * package's view registry, keyed by the same `kind`).
 *
 * A lifecycle capability owns everything the state layer needs to drive an
 * approval proposal end-to-end:
 *
 *  - `parseToolStart` / `parseToolComplete`: turn the agent's SSE tool
 *    events into in-flight proposals (and hydrate canonical ids on
 *    completion). Routing in `run-agent-orchestrator.ts` looks the
 *    capability up by `toolName` so adding a new approval-gated tool needs
 *    no change to the SSE loop.
 *  - `apply` / `reject`: thunks that hit the capability's own endpoints and
 *    return a normalized outcome (plus opaque `settledMeta` merged onto the
 *    proposal). The generic approval panel orchestrates plan-resume around
 *    them without knowing the capability's transport.
 *  - `buildResumeRowLine`: the per-row text for the synthetic follow-up
 *    turn the orchestrator receives once every row of a plan settles.
 *
 * `cypher_write` is the first registered capability; see
 * `cypher-write-capability.ts`.
 */
import type {
  AgentActionTerminalState,
  InFlightAgentActionProposal
} from '../../slices/agent-orchestrator-slice'
import type { AppDispatch } from '../../store'

/** Canonical planId/writeId envelope parsed from a propose tool's output. */
export interface AgentActionCompleteHydration {
  parentToolUseId: string
  planId: string
  results: Array<{ toolUseId: string; writeId: string; index?: number }>
}

/**
 * How `tool_call_complete` for a blocking propose tool resolves the
 * plan's in-flight cards.
 *
 * - `retire`: the plan settled in-turn (every row decided, outcome
 *   delivered inside the same agent turn — including the already-relayed
 *   case where the fallback rail won the claim). The cards are done;
 *   the agent's continuation text is the record.
 * - `keep`: the wait timed out server-side. The rows are still
 *   `awaiting_user`, the cards stay actionable, and the fallback resume
 *   rail owns outcome delivery from here.
 */
export type AgentActionSettledCompletion = { kind: 'retire'; planId: string } | { kind: 'keep' }

/**
 * Parse the settled envelope a blocking propose tool returns once the
 * user has decided every row of the plan. Returns `null` when the output
 * is not a settled envelope (legacy `pending: true` async-suspend
 * responses and validation failures fall through to
 * `parseToolComplete`). Shared by every capability whose tool blocks on
 * the agent-writes settlement wait — the envelope shape is identical
 * across cypher, node-evidence, ServiceNow, and writeback-tool proposes.
 */
export function parseSettledProposeOutput(output: unknown): AgentActionSettledCompletion | null {
  if (!output || typeof output !== 'object' || Array.isArray(output)) return null
  const obj = output as Record<string, unknown>
  if (obj.timed_out === true) return { kind: 'keep' }
  if (obj.settled !== true) return null
  const planId = obj.plan_id
  if (typeof planId !== 'string' || planId.length === 0) return null
  return { kind: 'retire', planId }
}

/**
 * Outcome of a single apply mutation. `transport_error` keeps the card
 * actionable (retry); the other two are terminal. `settledMeta` is merged
 * onto the proposal by `markAgentActionSettled`. `planWide` marks a
 * failure that applies to every row of the plan identically (e.g. the
 * branch-protection refusal) so the panel can settle the whole plan and
 * fire the resume turn instead of leaving sibling cards undecided.
 */
export type AgentActionApplyOutcome =
  | { kind: 'success'; settledMeta?: Record<string, unknown> }
  | {
      kind: 'apply_failed'
      error: string
      settledMeta?: Record<string, unknown>
      planWide?: boolean
    }
  | { kind: 'transport_error'; error: string }

export type AgentActionRejectOutcome =
  | { kind: 'success'; settledMeta?: Record<string, unknown> }
  | { kind: 'reject_failed'; error: string }
  | { kind: 'transport_error'; error: string }

/** Optional context forwarded from the approval panel into capability.apply. */
export interface AgentActionApplyOptions {
  /** Ingest job branch — Apply must not hit main/protected. */
  branchId?: string
}

export interface AgentActionLifecycleCapability {
  /** Discriminator shared with the proposal's `kind` and the view capability. */
  kind: string
  /** SSE tool name that emits proposals of this kind. */
  toolName: string
  /**
   * Additional SSE tool names routed to this capability. Used when
   * several propose tools share one proposal kind — `propose_node_evidence`
   * persists `cypher_write`-shaped rows, so its events route to the
   * cypher capability rather than a parallel registration.
   */
  aliasToolNames?: string[]
  /**
   * Additional proposal `kind` values that share this capability's apply /
   * reject / resume handlers. Used when one tool emits a successor kind
   * (e.g. `graph_write`) while still owning the same lifecycle endpoints.
   */
  aliasKinds?: string[]

  /** Parse a `tool_call_start` input into one-or-more in-flight proposals. */
  parseToolStart(
    toolUseId: string,
    input: unknown,
    startedAtMs: number
  ): { id: string; proposal: InFlightAgentActionProposal }[]

  /**
   * Parse a `tool_call_complete` output into the canonical id hydration,
   * or `null` when the tool failed (validation/persistence) — `null`
   * tells routing to drop the never-actionable cards for that parent.
   * Only consulted after `parseSettledToolComplete` declined the output.
   */
  parseToolComplete(toolUseId: string, output: unknown): AgentActionCompleteHydration | null

  /**
   * Parse a blocking propose tool's settled `tool_call_complete` output.
   * `retire` removes the plan's cards (the decisions were delivered
   * in-turn); `keep` leaves them actionable (server-side wait timed out;
   * the fallback resume rail owns them now); `null` falls through to the
   * legacy `parseToolComplete` routing (async-suspend `pending: true`
   * envelopes and validation failures). Absent on capabilities whose
   * tools never block.
   */
  parseSettledToolComplete?(output: unknown): AgentActionSettledCompletion | null

  /** Optional retry-poll of the capability's pending endpoint after start. */
  scheduleHydratePending?(dispatch: AppDispatch, conversationId: string | null | undefined): void

  /** Optional per-apply context (e.g. ingest job branch). */
  apply(
    toolUseId: string,
    options?: AgentActionApplyOptions
  ): (dispatch: AppDispatch) => Promise<AgentActionApplyOutcome>

  /** Reject a single proposal with a reason. */
  reject(
    toolUseId: string,
    reason: string
  ): (dispatch: AppDispatch) => Promise<AgentActionRejectOutcome>

  /** One line per settled row in the synthetic plan-resume instruction. */
  buildResumeRowLine(proposal: InFlightAgentActionProposal, displayNumber: number): string
}

/**
 * Read a structured refusal off a rejected apply/reject mutation. The
 * api-service answers refused/failed applies with HTTP 409 + `{ ok: false,
 * error, code? }`; RTK Query surfaces non-2xx as a *rejected* mutation with
 * the body on `err.data`. Without this, a refusal (e.g. "main is
 * protected") looks like a network blip: the card stays actionable and the
 * plan-resume turn never fires. Returns the error text + optional machine
 * code, or `null` for genuine transport failures.
 */
export function readStructuredMutationError(err: unknown): { error: string; code?: string } | null {
  if (!err || typeof err !== 'object') return null
  const data = (err as { data?: unknown }).data
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null
  const record = data as Record<string, unknown>
  if (record.ok !== false) return null
  return {
    error:
      typeof record.error === 'string' && record.error.trim().length > 0
        ? record.error
        : 'The server refused the request.',
    code: typeof record.code === 'string' ? record.code : undefined
  }
}

/** Refusal codes that fail every row of the plan identically. */
const PLAN_WIDE_REFUSAL_CODES = new Set(['BRANCH_PROTECTED', 'BRANCH_RESOLVE_FAILED'])

export function isPlanWideRefusalCode(code: string | undefined): boolean {
  return code !== undefined && PLAN_WIDE_REFUSAL_CODES.has(code)
}

const lifecycleByKind = new Map<string, AgentActionLifecycleCapability>()
const lifecycleByToolName = new Map<string, AgentActionLifecycleCapability>()

export function registerAgentActionLifecycle(capability: AgentActionLifecycleCapability): void {
  lifecycleByKind.set(capability.kind, capability)
  for (const alias of capability.aliasKinds ?? []) {
    lifecycleByKind.set(alias, capability)
  }
  lifecycleByToolName.set(capability.toolName, capability)
  for (const alias of capability.aliasToolNames ?? []) {
    lifecycleByToolName.set(alias, capability)
  }
}

export function getAgentActionLifecycle(kind: string): AgentActionLifecycleCapability | undefined {
  return lifecycleByKind.get(kind)
}

export function getAgentActionLifecycleByToolName(
  toolName: string
): AgentActionLifecycleCapability | undefined {
  return lifecycleByToolName.get(toolName)
}

/** Build the resume row for a proposal, delegating to its capability. */
export function resumeRowLineFor(
  proposal: InFlightAgentActionProposal,
  displayNumber: number
): string {
  const capability = getAgentActionLifecycle(proposal.kind)
  if (capability) return capability.buildResumeRowLine(proposal, displayNumber)
  // Defensive fallback for an unregistered kind — keep the resume turn
  // readable rather than throwing.
  const terminal = proposal.terminalState
  const verb = terminal?.kind ? terminal.kind.toUpperCase() : 'UNDECIDED'
  return `${displayNumber}. ${verb} — "${proposal.summary}".`
}

export type { AgentActionTerminalState }
